import React, { useState } from 'react';
import { Box, Button, ButtonSize, MyChoiceIcon } from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';

import { IChangeGroupActions } from '@epi-models/pages/Beneficiaries';
import { useChangeBeneficiaryGroupMutation } from '@epi-api/benefitGroups/benefitGroupsApi';
import { showNotification } from '@epi-actions/overlays';
import { useGetDetailsQuery } from '@epi-api/user';

import { ChangeGroupModal } from './ChangeGroupModal';
import { StyledActionBox } from './ChangeGroupActions.styles';

export const ChangeGroupActions = ({
  allBeneficiaries,
  selectedBeneficiaryIds,
  disableChangeGroup,
  setSelectedBeneficiaryIds
}: IChangeGroupActions) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number>();
  const { fontSemiBold, sizeSmall, gray0 } = useTheme();
  const [changeBeneficiaryGroup, { isLoading }] =
    useChangeBeneficiaryGroupMutation();
  const { data: { isGroupManagementAllowed } = {} } = useGetDetailsQuery();

  const handleCloseModal = () => {
    setIsOpenModal(false);
    setSelectedGroupId(undefined);
  };

  const handleConfirm = async (selectedGroupId: number) => {
    setIsOpenModal(false);
    const payload = {
      personIds: selectedBeneficiaryIds,
      newGroupId: selectedGroupId
    };

    try {
      const response = await changeBeneficiaryGroup(payload).unwrap();
      setSelectedBeneficiaryIds([]);
      setSelectedGroupId(0);
      console.log('response', response);
      dispatch(
        showNotification(
          {
            message: 'containers.beneficiaries.table.change_group_success',
            numberOfSelected: response.affectedBeneficiaries
          },
          'success'
        )
      );
    } catch (error) {
      dispatch(showNotification({ message: 'messages.error' }, 'error'));
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={0.5}
      py={2}
      fontSize={sizeSmall}
    >
      <Box display="flex" gap={0.5}>
        <Trans
          i18nKey="containers.beneficiaries.table.found_counter"
          values={{ numberOfEmployees: allBeneficiaries.length }}
          components={{
            bold: <Box fontWeight={fontSemiBold} />
          }}
        />
      </Box>
      <StyledActionBox
        isGroupManagementAllowed={isGroupManagementAllowed}
        py={1.5}
        px={2}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          {isGroupManagementAllowed ? (
            <Trans
              i18nKey="containers.beneficiaries.table.selected_counter"
              values={{ numberOfEmployees: selectedBeneficiaryIds.length }}
              components={{
                bold: <Box fontWeight={fontSemiBold} />
              }}
            />
          ) : (
            t('containers.benefit_groups.processing_of_employees.warning')
          )}
          <Box ml={1.5}>
            <Button
              id="change-benefit-group-button"
              size={ButtonSize.Small}
              disabled={disableChangeGroup || !isGroupManagementAllowed}
              onClick={() => setIsOpenModal(true)}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <MyChoiceIcon color={gray0} />
                {t('containers.beneficiaries.table.change_group_button')}
              </Box>
            </Button>
          </Box>
        </Box>
      </StyledActionBox>
      <ChangeGroupModal
        isOpen={isOpenModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirm}
        changeInProgress={isLoading}
        selectedGroupId={selectedGroupId}
        setSelectedGroupId={setSelectedGroupId}
      />
    </Box>
  );
};
